import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneRight, CaretDown, CaretUp, Warning } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton } from "./Brutal";

/**
 * v403.9 — Email Log panel (admin dashboard, owner/admin only).
 * Shows every email the app tried to send (low-stock digests, credentials,
 * tests) with sent/failed status, sender used (incl. fallback), and errors —
 * so the owner can confirm alerts actually went out. Includes a
 * "Send test email" button.
 */
export function EmailLogCard() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const load = () => {
    api.get("/admin/email-log", { params: { limit: 50 } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  };
  useEffect(load, []);

  if (!data) return null;

  const fmtWhen = (iso) =>
    iso ? new Date(iso).toLocaleString(i18n.language === "ru" ? "ru-RU" : "en-US",
      { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

  const kindLabel = (k) => t(`email_log.kind_${k}`, { defaultValue: k });

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data: res } = await api.post("/admin/test-email", {});
      toast.success(
        res.fallback
          ? t("email_log.test_sent_fallback", { sender: res.sender })
          : t("email_log.test_sent", { sender: res.sender })
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally {
      setSendingTest(false);
    }
  };

  const visible = expanded ? data.items : data.items.slice(0, 8);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 sm:p-5" data-testid="email-log-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#fbf6e7] border border-[#c9a24a] text-[#8a6a1c] flex items-center justify-center">
            <EnvelopeSimple size={16} weight="bold" />
          </div>
          <div>
            <h3 className="font-semibold text-base leading-tight">{t("email_log.title")}</h3>
            <p className="text-xs text-zinc-500">
              <span className="text-emerald-700 font-semibold" data-testid="email-log-sent-count">
                {t("email_log.sent_n", { n: data.sent_total })}
              </span>
              {" · "}
              <span className={data.failed_total > 0 ? "text-rose-700 font-semibold" : ""} data-testid="email-log-failed-count">
                {t("email_log.failed_n", { n: data.failed_total })}
              </span>
            </p>
          </div>
        </div>
        <BrutalButton size="sm" variant="secondary" onClick={sendTest} disabled={sendingTest}
          data-testid="email-log-send-test">
          <PaperPlaneRight size={13} weight="bold" />
          {sendingTest ? t("common.saving") : t("email_log.send_test")}
        </BrutalButton>
      </div>

      {data.items.length === 0 ? (
        <p className="text-xs text-zinc-500 mt-3" data-testid="email-log-empty">{t("email_log.empty")}</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          {visible.map((m, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg border border-zinc-100 px-2.5 py-1.5"
              data-testid={`email-log-row-${i}`}>
              <span className={`mt-0.5 shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
                m.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                     : "bg-rose-50 text-rose-700 border-rose-200"
              }`}>
                {m.ok ? t("email_log.status_sent") : t("email_log.status_failed")}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-zinc-800 truncate">
                  {kindLabel(m.kind)} → {m.to}
                  {m.fallback && (
                    <span className="ml-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                      {t("email_log.fallback_chip")}
                    </span>
                  )}
                </div>
                {!m.ok && m.error && (
                  <div className="text-[11px] text-rose-600 truncate flex items-center gap-1" title={m.error}>
                    <Warning size={11} weight="bold" className="shrink-0" /> {m.error}
                  </div>
                )}
              </div>
              <span className="shrink-0 text-[11px] text-zinc-400 tabular-nums">{fmtWhen(m.at)}</span>
            </div>
          ))}
          {data.items.length > 8 && (
            <button type="button" onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#8a6a1c] hover:underline"
              data-testid="email-log-expand">
              {expanded ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
              {expanded ? t("email_log.show_less") : t("email_log.show_all", { n: data.items.length })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
